/* eslint-disable react/prop-types */
import * as React from 'react';

import { useBoolean, useLockFn, useUpdateEffect } from 'ahooks';
import {
  Avatar,
  Badge,
  Button,
  Col,
  Divider,
  Dropdown,
  Flex,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Space,
} from 'antd';

import {
  AntdRouterExternalLink,
  AntdRouterLink,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  KeyOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveForm,
  PrimitiveTable,
  UserOutlinedIcon,
} from 'components';
import {
  changeUserPassword,
  deleteUser,
  sendUserForgetPasswordLink,
} from 'services';
import type { UserSummary } from 'types';
import {
  adjustPageNumber,
  generateDownloadFile,
  generateTableDataSource,
  inputRule,
  PATHNAME,
} from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    USERS: { UPDATE_URL: USERS_UPDATE_URL },
  },
  QUERY_PARAM: {
    DEFAULT: { AT_SIGN: DEFAULT_AT_SIGN },
  },
} = PATHNAME;

type UsersTableProps = {
  loading: boolean;
  total: number;
  users: UserSummary[];
  pageSize: number;
  pageNumber: number;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleRefreshData: () => void;
};

const UsersTable = React.memo<UsersTableProps>(
  ({
    loading,
    total,
    users,
    pageSize,
    pageNumber,
    handlePageSize,
    handlePageNumber,
    handleRefreshData,
  }) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const handleDeleteUser = useLockFn(async ({ id }: UserSummary) => {
      deleteUser({ id })
        .then(() => {
          handleRefreshData();
          notification.success({
            message: 'کاربر مورد نظر با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        });
    });

    const usersDataSource = generateTableDataSource(users, 'id');

    return (
      <PrimitiveTable
        columns={[
          {
            title: 'اطلاعات',
            dataIndex: 'id',
            key: 'id',
            ellipsis: true,
            render: (_, { id, displayName, firstName, lastName, avatarId }) => (
              <Space>
                <Avatar
                  src={avatarId ? generateDownloadFile('User', avatarId) : null}
                  icon={<UserOutlinedIcon />}
                />
                <AntdRouterLink
                  to={`${USERS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                  className='text-wrap'
                >
                  {displayName ||
                    `${firstName || 'بدون نام'} ${lastName || ''}`}
                </AntdRouterLink>
              </Space>
            ),
          },
          {
            title: 'شماره همراه',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            ellipsis: true,
            render: (_, { phoneNumber }) => (
              <AntdRouterExternalLink href={`tel:${phoneNumber}`}>
                <bdi className='font-mono'>{phoneNumber}</bdi>
              </AntdRouterExternalLink>
            ),
          },
          {
            title: 'نام کاربری',
            dataIndex: 'userName',
            key: 'userName',
            ellipsis: true,
            render: (_, { userName }) => (
              <span>{userName ? userName.concat(DEFAULT_AT_SIGN) : ''}</span>
            ),
          },
          {
            title: 'نوع دسترسی',
            dataIndex: 'roleName',
            key: 'roleName',
            width: 110,
            ellipsis: true,
            render: (_, { roleName }) => <Badge text={roleName} color='blue' />,
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'user-password-management',
                          label: <UserPasswordModal {...record} />,
                        },
                        {
                          key: 'update-user',
                          label: (
                            <AntdRouterLink
                              to={`${USERS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>ویرایش کاربر</span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-user',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف کاربر مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeleteUser(record);
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>حذف کاربر</span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={usersDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
UsersTable.displayName = 'UsersTable';

type UserPasswordFieldType = Record<'newPassword' | 'confirmPassword', string>;

type UserPasswordModalProps = UserSummary;

const UserPasswordModal = React.memo<UserPasswordModalProps>(props => {
  const { id } = props;
  const [open, { toggle: toggleOpen }] = useBoolean();
  const [loading, { toggle: toggleLoading }] = useBoolean();
  const [form] = Form.useForm<UserPasswordFieldType>();

  const onFinish = useLockFn(async ({ newPassword }: UserPasswordFieldType) => {
    toggleLoading();

    changeUserPassword({ id }, { newPassword })
      .then(() => {
        notification.success({
          message: 'رمز کاربر با موفقیت به‌روزرسانی شد.',
        });
        form.resetFields();
      })
      .catch(message => {
        notification.error({
          message,
        });
      })
      .finally(() => {
        toggleLoading();
        toggleOpen();
      });
  });

  return (
    <React.Fragment>
      <Button
        type='text'
        size='small'
        className='my-0.5 flex items-center px-0'
        onClick={toggleOpen}
      >
        <KeyOutlinedIcon />
        <span className='text-sm'>مدیریت رمز کاربر</span>
      </Button>
      <Modal
        centered
        width={640}
        open={open}
        title='به‌روزرسانی رمز عبور'
        modalRender={node => (
          <PrimitiveForm<UserPasswordFieldType>
            clearOnDestroy
            form={form}
            onFinish={onFinish}
          >
            {node}
          </PrimitiveForm>
        )}
        footer={(_, { OkBtn, CancelBtn }) => (
          <Flex gap='small'>
            <UserPasswordForgetLink {...props} />
            <CancelBtn />
            <OkBtn />
          </Flex>
        )}
        okButtonProps={{
          loading,
          autoFocus: true,
          htmlType: 'submit',
        }}
        onCancel={toggleOpen}
      >
        <Divider />
        <Row>
          <Col span={24}>
            <Form.Item<UserPasswordFieldType>
              label='رمز عبور جدید'
              name='newPassword'
              rules={[
                inputRule.required,
                inputRule.password,
                inputRule.nonWhitespace,
              ]}
            >
              <Input.Password placeholder='رمز عبور جدید را وارد کنید' />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item<UserPasswordFieldType>
              label='تکرار رمز عبور'
              name='confirmPassword'
              dependencies={['newPassword']}
              rules={[
                inputRule.required,
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || value === getFieldValue('newPassword')) {
                      return Promise.resolve();
                    }

                    return Promise.reject(
                      new Error('رمز عبور جدید و تکرار آن باید یکسان باشند!'),
                    );
                  },
                }),
              ]}
            >
              <Input.Password placeholder='تکرار رمز عبور را وارد کنید' />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
      </Modal>
    </React.Fragment>
  );
});
UserPasswordModal.displayName = 'UserPasswordModal';

type UserPasswordForgetLinkProps = UserSummary;

const UserPasswordForgetLink = React.memo<UserPasswordForgetLinkProps>(
  props => {
    const { id } = props;
    const [loading, { toggle: toggleLoading }] = useBoolean();

    const onClick = useLockFn(async () => {
      toggleLoading();

      sendUserForgetPasswordLink({ id })
        .then(() => {
          notification.success({
            message: 'لینک بازیابی با موفقیت ارسال شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        })
        .finally(() => {
          toggleLoading();
        });
    });

    return (
      <Button
        color='primary'
        variant='outlined'
        className='me-auto'
        loading={loading}
        onClick={onClick}
      >
        ارسال لینک بازیابی
      </Button>
    );
  },
);
UserPasswordForgetLink.displayName = 'UserPasswordForgetLink';

export { UserPasswordForgetLink, UserPasswordModal, UsersTable };
